import React, { useState } from "react";
import { updateOrder } from "../apis/auth"; // Import the updateOrder API function

const dropdownOptions = {
  bookingType: ["Container", "Break Bulk"],
  container: ["Organic", "Conventional", "Convencional/Organica"],
};

const formatLabel = (label) =>
  label.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

const EditOrderModal = ({ orderData, onClose, onUpdate }) => {
  const [formData, setFormData] = useState(orderData);

  const handleInputChange = (section, field, value) => {
    if (section) {
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleGoodsChange = (index, field, value) => {
    const updatedGoodsDetails = [...formData.goodsDetails];
    updatedGoodsDetails[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      goodsDetails: updatedGoodsDetails,
    }));
  };

  const addContainer = () => {
    setFormData((prev) => ({
      ...prev,
      goodsDetails: [
        ...prev.goodsDetails,
        {
          containerType: "",
          quantity: "",
          weight: "",
          description: "",
        },
      ],
    }));
  };

  const removeContainer = (index) => {
    if (formData.goodsDetails.length > 1) {
      setFormData((prev) => ({
        ...prev,
        goodsDetails: prev.goodsDetails.filter((_, i) => i !== index),
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await updateOrder(formData._id, formData);
      console.log("Order updated successfully:", response);
      alert("Order updated successfully!");
      onUpdate();

      onUpdate(response.data);
      onClose();
    } catch (error) {
      console.error("Error updating order:", error);
      alert("Failed to update order. Please try again.");
    }
  };

  const hiddenFields = ["_id", "createdAt", "updatedAt", "createdBy", "__v", "orderId"];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Edit Order</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {Object.keys(formData)
              .filter((key) => !hiddenFields.includes(key))
              .map((key) => {
                const value = formData[key];

                // Handle goodsDetails array specially
                if (key === "goodsDetails" && Array.isArray(value)) {
                  return (
                    <div key={key} className="md:col-span-2">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-700 border-b pb-2">
                          Goods Details
                        </h4>
                        {formData.bookingType === "Container" && (
                          <button
                            type="button"
                            onClick={addContainer}
                            className="text-sm text-blue-600 border border-blue-600 px-3 py-1 rounded-lg hover:bg-blue-50 transition"
                          >
                            + Add Container
                          </button>
                        )}
                        {formData.bookingType === "Break Bull" && (
                          <button
                            type="button"
                            onClick={addContainer}
                            className="text-sm text-blue-600 border border-blue-600 px-3 py-1 rounded-lg hover:bg-blue-50 transition"
                          >
                            + Add Row
                          </button>
                        )}
                      </div>
                      
                      {value.map((goods, index) => (
                        <div key={index} className="border border-gray-200 p-4 mb-4 rounded-lg bg-gray-50">
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="text-indigo-600 font-medium">
                              {formData.bookingType === "Container" ? `Container ${index + 1}` : `Goods Row ${index + 1}`}
                            </h5>
                            {value.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeContainer(index)}
                                className="text-red-500 hover:text-red-700 text-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            {Object.keys(goods).map((field) => (
                              <div key={field} className="flex flex-col gap-1">
                                <label className="text-sm text-gray-600">
                                  {formatLabel(field)}
                                </label>
                                <input
                                  type="text"
                                  className="border rounded px-3 py-2 text-sm"
                                  value={goods[field]}
                                  onChange={(e) => handleGoodsChange(index, field, e.target.value)}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }

                // Special handling for bookingType - make it read-only
                if (key === "bookingType") {
                  return (
                    <div key={key} className="flex flex-col gap-1">
                      <label className="text-sm text-gray-600">{formatLabel(key)}</label>
                      <div className="border rounded px-3 py-2 text-sm bg-gray-100 text-gray-700">
                        {formData[key] || "Not specified"}
                      </div>
                    </div>
                  );
                }

                if (dropdownOptions[key]) {
                  const options = new Set(dropdownOptions[key]);
                  if (formData[key]) options.add(formData[key]); // ensure current value is always present

                  return (
                    <div key={key} className="flex flex-col gap-1">
                      <label className="text-sm text-gray-600">{formatLabel(key)}</label>
                      <select
                        className="border rounded px-3 py-2 text-sm"
                        value={formData[key] || ""}
                        onChange={(e) => handleInputChange(null, key, e.target.value)}
                      >
                        <option value="">Select</option>
                        {[...options].map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }

                if (typeof formData[key] === "object" && formData[key] !== null) {
                  return (
                    <div key={key} className="md:col-span-2">
                      <h3 className="font-semibold mb-2 border-b pb-1">{formatLabel(key)}</h3>
                      <table className="w-full text-sm mb-4">
                        <tbody>
                          {Object.keys(formData[key])
                            .filter((nestedKey) => !["_id", "createdBy", "createdAt", "__v"].includes(nestedKey))
                            .map((nestedKey) => {
                              // Special handling for "Marks and No." field - only show for Break Bull
                              if (nestedKey === "marksAndNo" && formData.bookingType !== "Break Bull") {
                                return null;
                              }
                              
                              return (
                                <tr key={nestedKey} className="border-t">
                                  <td className="py-2 pr-4 font-medium text-gray-600 w-1/3">
                                    {nestedKey === "marksAndNo" ? "Marks and No." : formatLabel(nestedKey)}
                                  </td>
                                  <td className="py-2">
                                    <input
                                      type="text"
                                      className="w-full border rounded px-3 py-2"
                                      value={formData[key][nestedKey] || ""}
                                      onChange={(e) =>
                                        handleInputChange(key, nestedKey, e.target.value)
                                      }
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  );
                }

                return (
                  <div key={key} className="flex flex-col gap-1">
                    <label className="text-sm text-gray-600">{formatLabel(key)}</label>
                    <input
                      type="text"
                      className="border rounded px-3 py-2 text-sm"
                      value={formData[key]}
                      onChange={(e) => handleInputChange(null, key, e.target.value)}
                    />
                  </div>
                );
              })}
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-4 mt-6">
            <button
              onClick={onClose}
              className="border border-gray-300 px-6 py-2 rounded text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditOrderModal;
